import {
  ColumnFilter,
  ColumnFilters,
  FilterOperator,
  ParsedTable,
  SortedTableRow,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import { parseNumericValue } from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

export const EMPTY_CELL_LABEL = `(Empty)`;

export const TEXT_FILTER_OPERATORS: Array<{
  label: string;
  value: FilterOperator;
}> = [
  { label: `Contains`, value: `contains` },
  { label: `Does not contain`, value: `notContains` },
  { label: `Equals`, value: `equals` },
  { label: `Does not equal`, value: `notEquals` },
  { label: `Begins with`, value: `startsWith` },
  { label: `Ends with`, value: `endsWith` },
];

export const NUMERIC_FILTER_OPERATORS: Array<{
  label: string;
  value: FilterOperator;
}> = [
  { label: `Equals`, value: `equals` },
  { label: `Does not equal`, value: `notEquals` },
  { label: `Greater than`, value: `greaterThan` },
  { label: `Greater than or equal to`, value: `greaterThanOrEqual` },
  { label: `Less than`, value: `lessThan` },
  { label: `Less than or equal to`, value: `lessThanOrEqual` },
  { label: `Between`, value: `between` },
];

export function getDistinctColumnValues(
  table: ParsedTable,
  columnIndex: number,
  isNumeric: boolean,
) {
  const distinctValues = Array.from(
    new Set(table.rows.map((row) => (row[columnIndex] ?? ``).trim())),
  );

  return distinctValues.sort((first, second) => {
    if (!first || !second) {
      return first ? 1 : -1;
    }

    if (isNumeric) {
      return (
        (parseNumericValue(first) ?? Infinity) -
        (parseNumericValue(second) ?? Infinity)
      );
    }

    return first.localeCompare(second, undefined, { sensitivity: `base` });
  });
}

export function isColumnFilterActive(filter?: ColumnFilter) {
  if (!filter) {
    return false;
  }

  return Boolean(filter.selectedValues) || isRuleComplete(filter);
}

export function countActiveFilters(filters: ColumnFilters) {
  return Object.values(filters).filter(isColumnFilterActive).length;
}

export function filterTableRows(
  rows: SortedTableRow[],
  filters: ColumnFilters,
  numericColumnIndexes: number[],
) {
  const activeFilters = Object.entries(filters).filter(([, filter]) =>
    isColumnFilterActive(filter),
  );

  if (!activeFilters.length) {
    return rows;
  }

  return rows.filter((row) =>
    activeFilters.every(([columnIndex, filter]) =>
      matchesColumnFilter(
        (row.values[Number(columnIndex)] ?? ``).trim(),
        filter,
        numericColumnIndexes.includes(Number(columnIndex)),
      ),
    ),
  );
}

function isRuleComplete({ operator, ruleValue, ruleValueEnd }: ColumnFilter) {
  if (!operator || !ruleValue?.trim()) {
    return false;
  }

  return operator !== `between` || Boolean(ruleValueEnd?.trim());
}

function matchesColumnFilter(
  value: string,
  filter: ColumnFilter,
  isNumeric: boolean,
) {
  if (filter.selectedValues && !filter.selectedValues.includes(value)) {
    return false;
  }

  if (!isRuleComplete(filter)) {
    return true;
  }

  return isNumeric
    ? matchesNumericRule(value, filter)
    : matchesTextRule(value, filter);
}

function matchesTextRule(value: string, { operator, ruleValue }: ColumnFilter) {
  const cell = value.toLowerCase();
  const expected = ruleValue.trim().toLowerCase();

  switch (operator) {
    case `contains`:
      return cell.includes(expected);
    case `notContains`:
      return !cell.includes(expected);
    case `equals`:
      return cell === expected;
    case `notEquals`:
      return cell !== expected;
    case `startsWith`:
      return cell.startsWith(expected);
    case `endsWith`:
      return cell.endsWith(expected);
    default:
      return true;
  }
}

function matchesNumericRule(
  value: string,
  { operator, ruleValue, ruleValueEnd }: ColumnFilter,
) {
  const cell = parseNumericValue(value);
  const expected = parseNumericValue(ruleValue);
  const expectedEnd = parseNumericValue(ruleValueEnd ?? ``);

  if (expected === null) {
    return true;
  }

  if (cell === null) {
    return operator === `notEquals`;
  }

  switch (operator) {
    case `equals`:
      return cell === expected;
    case `notEquals`:
      return cell !== expected;
    case `greaterThan`:
      return cell > expected;
    case `greaterThanOrEqual`:
      return cell >= expected;
    case `lessThan`:
      return cell < expected;
    case `lessThanOrEqual`:
      return cell <= expected;
    case `between`:
      return (
        expectedEnd === null ||
        (cell >= Math.min(expected, expectedEnd) &&
          cell <= Math.max(expected, expectedEnd))
      );
    default:
      return true;
  }
}
